/**
 * 사흘 — **기기·보관가구 증거.**
 *
 * E02·E03·E15·E16·E23·E26·E27·E29·E30·E33·E35·E36·E47 이 쓰는 물건들. 복합기
 * (`mfp_printer`)·노트북(`laptop_open`)·태블릿(`tablet_dock`)·철제 캐비닛
 * (`steel_cabinet`)처럼 이미 있는 것은 만들지 않는다.
 *
 * **상표 금지가 이 파일의 첫 번째 규칙이다.** 로고·글자마크는 물론이고 특정 제품을
 * 지목하게 만드는 **형태 자체**를 쓰지 않는다 — 정사각형 카메라 섬, 노치, 물방울
 * 배열, 특유의 스탠드 실루엣 전부 금지. 대신 어느 제조사에나 있는 **범용 형태**만
 * 쓴다: 둥근 모서리 판, 세로 알약형 카메라 하우징, 무지 알루미늄 테두리. 화면에도
 * 앱 아이콘 격자를 그리지 않는다 — 말풍선과 시각만 있으면 「메시지 화면」으로 읽힌다.
 *
 * 앞뒤 규약: `auditFacing` 은 부품 **이름**으로 앞뒤를 본다. `문`·`서랍`·`앞판`·
 * `조작 패널` 은 -Z, `뒤판`·`등판` 은 +Z 여야 한다. 문을 열어 둘 때도 경첩을 Y 축으로
 * 돌리면 무게중심 z 는 계속 음수라 통과한다 — 90° 를 넘겨도 마찬가지다.
 */

import * as THREE from 'three';
import { bevelBox, lathe, type MeshWriter } from './kernel';
import { paintGeometry, type Piece } from './pieces';
import { printedFace, litFace, rnd, shard, ribbon, spline, stroke } from './saholEvidence';

/* ------------------------------------------------------------------ *
 * 도구
 * ------------------------------------------------------------------ */

/** 유약통 정점색 팔레트 — 청자·백자·철화·잿물. 채도를 낮게 둬야 도자기로 읽힌다 */
const JAR_TINT = ['#dfe3dc', '#b9c7bd', '#cfc6ae', '#8f9a93', '#a8886a', '#e2ddd2', '#6f7a74'];

function num(
  g: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  px: number,
  color: string,
  align: CanvasTextAlign = 'left',
) {
  g.fillStyle = color;
  g.font = `600 ${px}px "DejaVu Sans", Arial, Helvetica, sans-serif`;
  g.textAlign = align;
  g.textBaseline = 'alphabetic';
  g.fillText(text, x, y);
  g.textAlign = 'left';
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

/** -Z 를 보는 인쇄면. `PlaneGeometry` 는 +Z 를 보므로 반드시 뒤집는다 */
function facingFront(w: number, h: number) {
  const p = new THREE.PlaneGeometry(w, h);
  p.rotateY(Math.PI);
  return p;
}

function facingUp(w: number, d: number) {
  const p = new THREE.PlaneGeometry(w, d);
  p.rotateX(-Math.PI / 2);
  // 보는 사람은 -Z 에 선다. rotateX 만 하면 글의 위쪽이 -Z 를 향해 **180도 뒤집혀 읽힌다**
  p.rotateY(Math.PI);
  return p;
}

/**
 * 원통에 **둘러 붙이는** 인쇄 라벨. 평면을 병 앞에 세우면 「붙은 종이」가 아니라
 * 「병 앞에 떠 있는 판」으로 보인다 — 실제로 그렇게 나왔다. 열린 원통 조각으로
 * 만들어 몸통 곡률을 그대로 타게 한다.
 *
 * `theta` 는 감기는 각(rad). 원통 UV 는 u 가 theta 를 따라 0→1 이고 theta 가
 * 커지는 방향이 -X 라, -Z 에 선 사람 기준으로 글이 왼→오른쪽으로 바로 읽힌다.
 * 그래서 `facingFront` 같은 뒤집기가 필요 없다.
 */
function wrapLabel(r: number, theta: number, h: number) {
  return new THREE.CylinderGeometry(r, r, h, Math.max(6, Math.round(theta * 9)), 1, true, Math.PI - theta / 2, theta);
}

/* ------------------------------------------------------------------ *
 * 물건
 * ------------------------------------------------------------------ */

/**
 * **서류함 서랍 한 짝의 형태** — 조각(`lock_cabinet_drawer`)과 서류함 몸통이 **같은 코드**를 쓴다.
 * 몸통에 서랍을 안기면서(2026-09-11) 지오메트리를 복사하면 둘이 갈라진다 — 한쪽만 고치면
 * 나머지가 옛 모습으로 남는다. 그래서 함수 하나로 두고 양쪽이 부른다.
 */
/** 서류함 서랍 한 칸 — 앞판(오목 손잡이) + 상자 + 세워 꽂은 서류철 + 색인 탭.
 * `lock_cabinet` 과 `steel_cabinet` 이 같이 쓴다. 복사하면 둘이 갈라진다 */
export function lockDrawerBuild(w: MeshWriter, d: Record<string, number>) {
      const T = 0.008;
      /* 상자는 앞판보다 좁다(레일 틈). **몸통 안쪽 폭보다 넓으면 벽을 뚫고 나와** 옆면에
       * 얼룩이 진다(2026-09-12 철제 캐비닛 side 컷) — 벽이 두꺼운 몸통은 `boxW` 로 따로 준다 */
      const bw = d.boxW ?? d.w - 0.014;
      const bd = d.d - d.face;

      w.part('앞판');
      /* **손잡이 구멍을 실제로 낸다.** 통판을 세우고 오목판을 뒤에 붙였더니 앞에서 안 보여
       * 앞판이 밋밋한 철판이었다(시트 2026-09-09). 서류함 서랍은 오목 손잡이가 얼굴이다 —
       * 판을 넷으로 나눠 가운데를 비우고, 그 뒤에 오목판을 12mm 뒤로 앉힌다 */
      const oW = 0.15, oH = 0.038, oy = d.h * 0.66;   // 손잡이 구멍
      const fz = -d.d / 2 + d.face / 2;
      const panels: [number, number, number, number][] = [
        [0, (oy + oH / 2 + d.h) / 2, d.w, d.h - oy - oH / 2],   // 위
        [0, (oy - oH / 2) / 2, d.w, oy - oH / 2],               // 아래
        [-(oW + (d.w - oW) / 2) / 2, oy, (d.w - oW) / 2, oH],   // 왼
        [(oW + (d.w - oW) / 2) / 2, oy, (d.w - oW) / 2, oH],    // 오른
      ];
      for (const [px, py, pw, ph] of panels) {
        const g = bevelBox(pw, ph, d.face, 0.003);
        g.translate(px, py, fz);
        w.add(g, 'steel', 0.35);
      }

      w.part('상자');
      // 옆판 둘 · 바닥 · 뒤판. 앞판보다 낮아 위가 트여 있다(서류를 넣는 곳)
      const bh = d.h - 0.045;
      for (const s of [-1, 1] as const) {
        const side = bevelBox(T, bh, bd, 0.002);
        side.translate(s * (bw / 2 - T / 2), bh / 2 + 0.012, d.face / 2);
        w.add(side, 'steel', 0.3);
      }
      const bot = bevelBox(bw - T * 2, T, bd, 0.002);
      bot.translate(0, 0.012 + T / 2, d.face / 2);
      w.add(bot, 'steel', 0.3);
      const back = bevelBox(bw - T * 2, bh, T, 0.002);
      back.translate(0, bh / 2 + 0.012, d.d / 2 - T / 2);
      w.add(back, 'steel', 0.3);

      w.part('서류철');
      /* **세워서** 꽂는다. 서류함 서랍은 서류가 서 있는 곳이다.
       * 뒤로 갈수록 조금씩 기울어 앞이 성기다 — 꽉 찬 서랍은 뒤진 흔적이 안 보인다.
       * 탭이 매달릴 수 있게 높이·기울기·자리를 **먼저 배열에 담는다**(아래 참조) */
      const folders: { fh: number; z: number; tilt: number }[] = [];
      for (let i = 0; i < 9; i++) {
        const fh = 0.19 + rnd(i * 3.1) * 0.03;
        const tilt = -0.05 - rnd(i * 5.3) * 0.07;
        const z = -bd * 0.28 + i * 0.038 + rnd(i * 7.7) * 0.008;
        folders.push({ fh, z, tilt });
        const fl = bevelBox(bw - 0.03, fh, 0.004, 0.0012);
        fl.rotateX(tilt);
        fl.translate(0, 0.012 + T + fh / 2, z);
        paintGeometry(fl, i % 3 === 0 ? '#c9bda2' : '#ded6c2');
        w.add(fl, 'paper', 0.2);
      }

      w.part('색인 탭');
      /* **탭 높이를 고정값으로 찍으면 뜬다**(joint-check 15mm, 2026-09-09).
       * 서류철 높이가 0.19~0.22 로 제각각이라 낮은 철 위의 탭이 허공에 남았다 —
       * 탭은 제 서류철의 `fh`·`z`·`tilt` 에서 유도한다(api.md 4). 윗변에 13mm 물린다 */
      [1, 3, 5, 7].forEach((fi, k) => {
        const f = folders[fi];
        const tab = bevelBox(0.056, 0.026, 0.0032, 0.0009);
        tab.rotateX(f.tilt);
        tab.translate(-bw * 0.3 + k * 0.092, 0.012 + T + f.fh - 0.006, f.z);
        w.add(tab, k === 1 ? 'redcloth' : 'paper', 0.2);
      });

      w.part('손잡이');
      // 구멍 뒤로 12mm 들어간 오목판 + 위쪽 걸림턱. 손가락이 걸리는 자리다
      const cupZ = fz + d.face / 2 + 0.012;
      const cup = bevelBox(oW, oH + 0.01, 0.004, 0.001);
      cup.translate(0, oy, cupZ);
      w.add(cup, 'alu', 0.15);
      for (const s of [-1, 1] as const) {
        const lip = bevelBox(oW, 0.005, 0.013, 0.0015);
        lip.translate(0, oy + s * (oH / 2 + 0.0015), cupZ - 0.006);
        w.add(lip, 'alu', 0.15);
      }

      w.part('라벨 홀더');
      // 손잡이 **바로 아래 가운데**. 좌하단에 치우치면 캐비닛 라벨이 아니라 스티커로 보인다
      const ly = oy - 0.062;
      const hold = bevelBox(0.092, 0.028, 0.004, 0.0012);
      hold.translate(0, ly, -d.d / 2 - 0.002);
      w.add(hold, 'alu', 0.1);
      w.thinSurface(() => {
        const pl = facingFront(0.086, 0.022);
        pl.translate(0, ly, -d.d / 2 - 0.0045);
        w.add(pl, 'painted', 0);
      });
}

export const SAHOL_DEVICE_PIECES: Piece[] = [
  {
    id: 'smartphone',
    name: '휴대폰',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E03·E30·E35·E48. 화면을 위로 두고 놓인 휴대폰. **어떤 상표도 쓰지 않는다** — 로고·노치·정사각 카메라 섬 금지. 둥근 알루미늄 테두리와 세로 알약형 카메라 하우징만으로 「휴대폰」은 충분히 읽힌다. 화면에는 아이콘 격자를 그리지 말고 말풍선과 22:34 만 남긴다. 그게 E30 의 전부다. */
    note: '화면을 위로 두고 놓인 휴대폰. **어떤 상표도 쓰지 않는다** — 로고·노치·정사각 카메라 섬 금지. 둥근 알루미늄 테두리와 세로 알약형 카메라 하우징만으로 「휴대폰」은 충분히 읽힌다. 화면에는 아이콘 격자를 그리지 않고 말풍선과 시계만 남긴다. dims 는 w·d 와 두께 t.',
    dims: { w: 0.0715, d: 0.1475, t: 0.0082 },
    /* 휴대폰을 집어 화면을 본다 (2026-09-16) */
    openable: [{ part: '휴대폰', slide: [0, 0, -0.04] }],
    clusters: 1,
    build(w, d) {
      w.movable('휴대폰', () => {
      w.part('몸체');
      const body = bevelBox(d.w, d.t, d.d, 0.0032, 2);
      body.translate(0, d.t / 2, 0);
      w.add(body, 'alu', 0.08);

      w.part('유리');
      const glass = bevelBox(d.w - 0.0016, d.t * 0.22, d.d - 0.0016, 0.0012, 2);
      glass.translate(0, d.t - d.t * 0.09, 0);
      w.add(glass, 'glass', 0.08);

      w.part('화면');
      w.thinSurface(() => {
        const p = facingUp(d.w - 0.005, d.d - 0.009);
        p.translate(0, d.t + 0.0003, 0);
        w.add(p, 'screen', 0);
      });

      w.part('카메라');
      // 몸통 **안으로** 파 넣는다. 튀어나오게 하면 바닥 아래로 내려가 접지 게이트에 걸린다
      const hous = bevelBox(0.019, d.t * 0.28, 0.036, 0.006, 2);
      hous.translate(-d.w * 0.28, d.t * 0.14, -d.d * 0.32);
      w.add(hous, 'plastic', 0.05);
      for (const dz of [-0.009, 0.009]) {
        const lens = lathe(
          [
            [0, 0],
            [0.0058, 0],
            [0.0058, 0.0016],
            [0.0042, 0.0022],
            [0, 0.0022],
          ],
          12,
        );
        lens.rotateX(Math.PI);
        lens.translate(-d.w * 0.28, d.t * 0.2, -d.d * 0.32 + dz);
        w.add(lens, 'glass', 0.05);
      }

      w.part('버튼');
      const vol = bevelBox(0.0016, 0.0026, 0.026, 0.0006);
      vol.translate(d.w / 2 - 0.0003, d.t * 0.55, -d.d * 0.12);
      w.add(vol, 'alu', 0.05);
      const pw = bevelBox(0.0016, 0.0026, 0.014, 0.0006);
      pw.translate(d.w / 2 - 0.0003, d.t * 0.55, d.d * 0.08);
      w.add(pw, 'alu', 0.05);
      });
    },
    materials: () => ({
      screen: litFace((g, W, H) => {
        // 상태줄 — 시각 하나. 통신사·배터리 아이콘을 그리면 곧바로 특정 기기가 된다
        num(g, '22:34', W * 0.5, H * 0.045, H * 0.026, '#dfe8f2', 'center');

        const bubbles: [number, number, number, boolean][] = [
          [0.1, 0.62, 0.1, false],
          [0.16, 0.5, 0.075, true],
          [0.245, 0.72, 0.115, false],
          [0.375, 0.42, 0.065, true],
          [0.455, 0.56, 0.09, false],
        ];
        for (const [y, wid, hgt, mine] of bubbles) {
          const bw = W * wid;
          const bh = H * hgt;
          const x = mine ? W * 0.94 - bw : W * 0.06;
          g.fillStyle = mine ? '#2f6ea8' : '#242a33';
          roundRect(g, x, H * y, bw, bh, Math.min(bw, bh) * 0.22);
          g.fill();
          g.strokeStyle = mine ? 'rgba(190,220,246,0.75)' : 'rgba(180,192,208,0.6)';
          for (let i = 0; i < Math.max(1, Math.floor(hgt / 0.028)); i++) {
            stroke(
              g,
              x + bw * 0.1,
              H * y + bh * 0.3 + i * H * 0.026,
              x + bw * (0.45 + rnd(i * 7 + y * 100) * 0.45),
              H * y + bh * 0.3 + i * H * 0.026,
              H * 0.007,
              i * 3,
            );
          }
        }
        // 마지막 말풍선만 밝다 — 22:34 에 온 그 메시지다
        g.fillStyle = '#3f86c8';
        roundRect(g, W * 0.06, H * 0.68, W * 0.66, H * 0.1, W * 0.03);
        g.fill();
        g.strokeStyle = 'rgba(232,244,255,0.95)';
        for (let i = 0; i < 3; i++) {
          stroke(g, W * 0.11, H * 0.71 + i * H * 0.028, W * (0.3 + rnd(i * 5) * 0.35), H * 0.71 + i * H * 0.028, H * 0.008, i);
        }
        num(g, '22:34', W * 0.74, H * 0.785, H * 0.024, 'rgba(190,206,224,0.8)');

        // 입력줄
        g.fillStyle = '#1a1f27';
        roundRect(g, W * 0.06, H * 0.88, W * 0.88, H * 0.07, H * 0.035);
        g.fill();
      }, 340, 700),
    }),
  },

  {
    id: 'group_photo',
    /** 벽에서 젖혀 뒤를 본다 — 액자 뒤는 숨기기 좋은 자리다 (2026-09-16) */
    openable: [{ part: '액자', swing: 1.1, axis: 'y', pivot: [-0.22, 0.165, 0.0] }],
    mount: 'wall',
    name: '단체사진 액자',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E02. 조합센터 벽의 「730일이 시작된 날」 단체사진. **원점이 걸이 지점이라 몸통이 y=0 아래로 내려간다** — hangs 와 sunk 를 둘 다 단다. 액자 오른쪽 아래 각인 2024.12.20 이 노트북 비밀번호를 여는 열쇠이므로 숫자만은 반드시 읽혀야 한다. 액자가 한쪽으로 2도쯤 기울어 있어야 「오래 걸려 있었다」가 된다. */
    note: '벽에 걸린 단체사진 액자. **원점이 걸이 지점이라 몸통이 y=0 아래로 내려간다** — hangs 와 sunk 를 둘 다 단다. 액자 오른쪽 아래에 날짜 각인이 있고 숫자는 읽힐 크기로 둔다. 액자가 한쪽으로 2도쯤 기울어 있어야 「오래 걸려 있었다」가 된다. dims 는 w·h, 두께 t, 프레임 폭 fr.',
    hangs: true,
    sunk: true,
    dims: { w: 0.44, h: 0.33, t: 0.03, fr: 0.024 },
    clusters: 1,
    build(w, d) {
      // 조각 전체가 하나의 여닫이 부품 — 벽에서 젖혀 뒤를 본다 (2026-09-16)
      w.movable('액자', () => {
      const tilt = 0.035;
      const cy = -d.h / 2;

      w.part('틀');
      const rim: [number, number, number, number][] = [
        [0, cy + d.h / 2 - d.fr / 2, d.w, d.fr],
        [0, cy - d.h / 2 + d.fr / 2, d.w, d.fr],
        [-d.w / 2 + d.fr / 2, cy, d.fr, d.h - d.fr * 2],
        [d.w / 2 - d.fr / 2, cy, d.fr, d.h - d.fr * 2],
      ];
      for (const [x, y, bw, bh] of rim) {
        const g = bevelBox(bw, bh, d.t, 0.003);
        g.rotateZ(tilt);
        g.translate(x * Math.cos(tilt) - (y - cy) * Math.sin(tilt), y + x * Math.sin(tilt), 0);
        w.add(g, 'walnut', 0.25);
      }

      w.part('뒤판');
      const back = bevelBox(d.w - 0.006, d.h - 0.006, 0.006, 0.002);
      back.rotateZ(tilt);
      back.translate(0, cy, d.t / 2 - 0.003);
      w.add(back, 'cardboard', 0.2);

      w.part('마운트');
      const mount = bevelBox(d.w - d.fr * 1.4, d.h - d.fr * 1.4, 0.004, 0.0015);
      mount.rotateZ(tilt);
      mount.translate(0, cy, d.t / 2 - 0.008);
      w.add(mount, 'paper', 0.2);

      w.part('사진면');
      w.thinSurface(() => {
        const p = facingFront(d.w - d.fr * 2.2, d.h - d.fr * 2.2);
        p.rotateZ(tilt);
        p.translate(0, cy, -d.t / 2 + 0.0055);
        w.add(p, 'painted', 0);
      });

      w.part('유리');
      const gl = bevelBox(d.w - d.fr * 1.8, d.h - d.fr * 1.8, 0.0022, 0.0006);
      gl.rotateZ(tilt);
      gl.translate(0, cy, -d.t / 2 + 0.004);
      w.add(gl, 'glass', 0.2);

      w.part('걸이');
      // 원점이 못 자리다. 고리가 원점을 물고 있어야 「여기 걸린다」가 형태로 보인다
      const hook = new THREE.TorusGeometry(0.0085, 0.0016, 5, 14);
      hook.rotateX(0.35);
      hook.translate(0, -0.006, d.t / 2 - 0.001);
      w.add(hook, 'steel', 0.1);
      const wire = bevelBox(d.w * 0.5, 0.0016, 0.0016, 0.0004);
      wire.translate(0, -0.03, d.t / 2 - 0.002);
      w.add(wire, 'steel', 0.1);
    });
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          const sky = g.createLinearGradient(0, 0, 0, H);
          sky.addColorStop(0, '#b9c6d2');
          sky.addColorStop(0.55, '#8f9dab');
          sky.addColorStop(1, '#5d6a72');
          g.fillStyle = sky;
          g.fillRect(0, 0, W, H);
          // 뒤쪽 건물 — 조합센터 앞에서 찍은 사진이다
          g.fillStyle = 'rgba(70,78,84,0.5)';
          g.fillRect(W * 0.08, H * 0.18, W * 0.84, H * 0.42);
          g.fillStyle = 'rgba(216,226,236,0.35)';
          for (let r = 0; r < 2; r++) {
            for (let c = 0; c < 6; c++) g.fillRect(W * (0.14 + c * 0.13), H * (0.24 + r * 0.16), W * 0.07, H * 0.1);
          }
          // 사람 열넷. 머리와 어깨만으로 단체사진이 된다 — 얼굴을 그리려 하면 무조건 망가진다
          for (let i = 0; i < 14; i++) {
            const row = i < 7 ? 0 : 1;
            const k = i % 7;
            const x = W * (0.1 + k * 0.128 + (row ? 0.06 : 0));
            const yb = H * (row ? 0.98 : 0.86);
            const hh = H * (0.26 + rnd(i) * 0.05);
            g.fillStyle = `rgba(${28 + rnd(i * 3) * 40 | 0},${30 + rnd(i * 5) * 36 | 0},${38 + rnd(i * 7) * 34 | 0},0.92)`;
            g.beginPath();
            g.moveTo(x - W * 0.05, yb);
            g.lineTo(x - W * 0.045, yb - hh * 0.72);
            g.lineTo(x + W * 0.045, yb - hh * 0.72);
            g.lineTo(x + W * 0.05, yb);
            g.closePath();
            g.fill();
            g.fillStyle = `rgba(${188 - rnd(i) * 46 | 0},${162 - rnd(i * 2) * 40 | 0},${142 - rnd(i * 4) * 36 | 0},0.95)`;
            g.beginPath();
            g.arc(x, yb - hh * 0.82, W * 0.028, 0, Math.PI * 2);
            g.fill();
          }
          // 각인. 이 여덟 자리가 E09 노트북 비밀번호다
          num(g, '2024.12.20', W * 0.96, H * 0.955, H * 0.058, '#f0a851', 'right');
        },
        900,
        660,
        '#9aa7b2',
      ),
    }),
  },

  {
    id: 'usb_drive',
    name: 'USB 메모리',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E36. 뚜껑이 벗겨진 채 놓인 USB. 상표는 없고 몸통에 **손으로 쓴 라벨 스티커** 한 장만 있다 — 이름표가 있어야 「누가 쓰던 것」이 된다. 커넥터의 금속 혓바닥과 그 안의 플라스틱 심이 보여야 장난감으로 안 보인다. 아주 작은 물건이라 모서리 라운드를 크게 주면 형태가 통째로 먹힌다. */
    note: '뚜껑이 벗겨진 채 놓인 USB 메모리. 상표는 없고 몸통에 **손으로 쓴 라벨 스티커** 한 장만 있다 — 이름표가 있어야 「누가 쓰던 것」이 된다. 커넥터의 금속 혓바닥과 그 안의 플라스틱 심이 보여야 장난감으로 안 보인다. 아주 작은 물건이라 모서리 라운드를 크게 주면 형태가 통째로 먹힌다. dims 는 w·h·d.',
    dims: { w: 0.019, h: 0.0086, d: 0.058 },
    clusters: 2,
    /* 벗겨져 옆에 굴러 있는 뚜껑을 집어 든다 */
    openable: [{ part: '뚜껑', slide: [0, 0.05, -0.02] }],
    build(w, d) {
      w.part('몸통');
      const body = bevelBox(d.w, d.h, d.d * 0.66, 0.0016, 2);
      body.translate(0, d.h / 2, d.d * 0.17);
      w.add(body, 'plastic', 0.05);

      w.part('커넥터');
      const shell = bevelBox(d.w * 0.64, d.h * 0.52, d.d * 0.36, 0.0006);
      shell.translate(0, d.h * 0.42, -d.d * 0.3);
      w.add(shell, 'alu', 0.04);
      const tongue = bevelBox(d.w * 0.46, d.h * 0.16, d.d * 0.26, 0.0003);
      tongue.translate(0, d.h * 0.34, -d.d * 0.3);
      w.add(tongue, 'plastic', 0.04);

      w.part('라벨');
      w.thinSurface(() => {
        const p = facingUp(d.w * 0.8, d.d * 0.44);
        p.translate(0, d.h + 0.0002, d.d * 0.18);
        w.add(p, 'painted', 0);
      });

      w.part('고리');
      const ring = new THREE.TorusGeometry(0.0038, 0.0009, 5, 12);
      ring.rotateX(Math.PI / 2);
      ring.translate(0, d.h * 0.5, d.d * 0.47);
      w.add(ring, 'alu', 0.04);

      w.part('뚜껑');
      // 벗겨진 뚜껑이 옆에 굴러 있다. 이게 없으면 「막 꽂았다 뺀 것」이 안 읽힌다
      w.movable('뚜껑', () => {
        const cap = bevelBox(d.w * 0.98, d.h * 0.9, d.d * 0.32, 0.0016, 2);
        cap.rotateY(0.55);
        cap.rotateZ(0.14);
        cap.translate(0.031, d.h * 0.45, -d.d * 0.06);
        w.add(cap, 'plastic', 0.05);
      });
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          g.strokeStyle = 'rgba(28,36,50,0.85)';
          for (let i = 0; i < 2; i++) {
            stroke(g, W * 0.1, H * (0.36 + i * 0.3), W * (0.5 + rnd(i) * 0.4), H * (0.36 + i * 0.3), H * 0.09, i * 5);
          }
        },
        256,
        128,
        '#e8e4d6',
      ),
    }),
  },

  {
    id: 'ext_hdd',
    name: '외장 하드',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E33. 케이블이 아직 꽂힌 채 놓인 2.5인치 외장 하드. 상표는 없고 알루미늄 몸통에 헤어라인만 있다. **케이블이 몸통에서 나와 바닥으로 늘어져 한 번 감겨 있어야** 「방금까지 연결돼 있었다」가 된다 — 케이블 없이 놓으면 그냥 검은 판이다. 접속 LED 한 점. */
    note: '케이블이 꽂힌 채 놓인 2.5인치 외장 하드. 상표는 없고 알루미늄 몸통에 헤어라인만 있다. **케이블이 몸통에서 나와 바닥으로 늘어져 한 번 감겨 있어야** 「방금까지 연결돼 있었다」가 된다 — 케이블 없이 놓으면 그냥 검은 판이다. 접속 LED 한 점. dims 는 w·h·d.',
    dims: { w: 0.083, h: 0.0155, d: 0.126 },
    /* 외장 하드를 집어 든다 — 케이블이 딸려 온다 (2026-09-16) */
    openable: [{ part: '외장 하드', slide: [0, 0, -0.05] }],
    clusters: 1,
    build(w, d) {
      w.movable('외장 하드', () => {
      w.part('몸통');
      const body = bevelBox(d.w, d.h, d.d, 0.0022, 2);
      body.translate(0, d.h / 2, 0);
      w.add(body, 'alu', 0.06);

      w.part('상판');
      w.thinSurface(() => {
        const p = facingUp(d.w - 0.004, d.d - 0.004);
        p.translate(0, d.h + 0.0002, 0);
        w.add(p, 'painted', 0);
      });

      w.part('밑창');
      // 고무발 넷. 이게 있어야 바닥에 「놓인」 것이 되고 없으면 바닥에 박힌 판이 된다
      for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
          const ft = bevelBox(0.008, 0.0014, 0.008, 0.0004);
          ft.translate((sx * (d.w - 0.018)) / 2, 0.0007, (sz * (d.d - 0.018)) / 2);
          w.add(ft, 'rubber', 0.04);
        }
      }

      w.part('LED');
      const led = lathe(
        [
          [0, 0],
          [0.0018, 0],
          [0.0018, 0.0008],
          [0, 0.0012],
        ],
        8,
      );
      led.translate(d.w * 0.34, d.h - 0.0004, d.d * 0.36);
      w.add(led, 'bulb', 0.04);

      w.part('케이블');
      const pts = spline(
        [
          [0, d.h * 0.4, d.d / 2 - 0.004],
          [0.004, d.h * 0.5, d.d / 2 + 0.03],
          [0.036, 0.0035, d.d / 2 + 0.052],
          [0.072, 0.0035, d.d / 2 + 0.018],
          [0.056, 0.0035, d.d / 2 - 0.03],
          [0.012, 0.0035, d.d / 2 - 0.052],
          [-0.05, 0.0035, d.d / 2 - 0.03],
          [-0.086, 0.0035, d.d / 2 + 0.03],
        ],
        30,
      );
      ribbon(w, pts, 0.0042, 0.0042, 'rubber');

      w.part('커넥터');
      const plug = bevelBox(0.011, 0.0055, 0.012, 0.0008);
      plug.translate(0, d.h * 0.42, d.d / 2 + 0.002);
      w.add(plug, 'alu', 0.04);
      });
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          // 헤어라인 — 알루미늄이 알루미늄으로 읽히는 가장 싼 방법이다
          for (let i = 0; i < 420; i++) {
            g.strokeStyle = `rgba(255,255,255,${0.02 + rnd(i) * 0.05})`;
            g.lineWidth = 0.6 + rnd(i * 3) * 1.2;
            const y = rnd(i * 7) * H;
            g.beginPath();
            g.moveTo(rnd(i * 11) * W * 0.4, y);
            g.lineTo(W * (0.5 + rnd(i * 13) * 0.5), y + (rnd(i) - 0.5) * 2);
            g.stroke();
          }
          g.fillStyle = 'rgba(0,0,0,0.16)';
          g.fillRect(0, 0, W, H * 0.03);
          g.fillRect(0, H * 0.97, W, H * 0.03);
        },
        512,
        720,
        '#8d949b',
      ),
    }),
  },

  {
    id: 'lock_cabinet',
    name: '잠금 서류함',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E15·E16. 재무실의 다이얼 잠금 2단 서류함의 **몸통**. **서랍은 -Z**. 오른쪽 잠금 바가 올라간 채다 — 잠겨 있어야 할 것이 열려 있다는 게 요점이므로 잠금 바의 위치를 반드시 「해제」로 둔다.
     * 앞이 트인 골조라 서랍 칸 둘이 비어 있다. **서랍은 `lock_cabinet_drawer` 두 짝**을 y=0.05 와 y=0.06+칸높이(0.375)에 넣는다 — 이 조각만 놓으면 서랍 없는 빈 함이다. */
    note: '다이얼 잠금이 달린 2단 서류함의 **몸통**. **서랍은 -Z**. 앞면 다이얼과 오른쪽 잠금 바가 「잠금 서류함」을 읽히게 하며, 잠금 바는 올라간 「해제」 위치다.\n앞이 트인 골조라 서랍 칸 둘이 비어 있다. **서랍은 `lock_cabinet_drawer` 두 짝**을 y=0.05 와 y=0.06+칸높이(0.375)에 넣는다 — 이 조각만 놓으면 서랍 없는 빈 함이다. dims 는 w·h·d 와 open.',
    /* 서랍 둘이 앞(−Z)으로 0.42m 미끄러진다. 위 서랍은 G02(1918)로 잠긴다 —
     * 잠금은 프롭의 `lock` 이 하고 여기서는 형태만 말한다. 담는 곳은 서류철 위 빈 자리 */
    openable: [
      { part: '아래 서랍', slide: [0, 0, -0.42], inside: { w: 0.4, h: 0.12, d: 0.5, at: [0, 0.2, 0.02] } },
      { part: '위 서랍', slide: [0, 0, -0.42], inside: { w: 0.4, h: 0.12, d: 0.5, at: [0, 0.52, 0.02] } },
    ],
    dims: { w: 0.47, h: 0.72, d: 0.62, open: 0.1 },
    clusters: 1,
    build(w, d) {
      w.part('몸통');
      /* **통짜 상자였다.** 서랍을 별도 조각(`lock_cabinet_drawer`)으로 뗀 이상 몸통에
       * 서랍이 들어갈 구멍이 있어야 한다 — 통짜면 서랍이 벽을 뚫고 나온다.
       * 앞(−Z)이 트인 골조로 바꾼다: 옆판 둘 · 뒤판 · 바닥판 · 천장판 · 칸막이 */
      const T = 0.012;
      const dh0 = (d.h - 0.09) / 2;                     // 서랍 칸 높이
      for (const s2 of [-1, 1] as const) {
        const side = bevelBox(T, d.h, d.d, 0.003);
        side.translate(s2 * (d.w / 2 - T / 2), d.h / 2, 0);
        w.add(side, 'steel', 0.4);
      }
      const back = bevelBox(d.w - T * 2, d.h, T, 0.003);
      back.translate(0, d.h / 2, d.d / 2 - T / 2);
      w.add(back, 'steel', 0.4);
      for (const [cy, nm] of [[0.05 - T / 2, '바닥'], [0.055 + dh0, '칸막이'], [d.h - T / 2, '천장']] as const) {
        void nm;
        const sh2 = bevelBox(d.w - T * 2, T, d.d - T, 0.003);
        sh2.translate(0, cy, -T / 2);
        w.add(sh2, 'steel', 0.4);
      }

      w.part('상판');
      const top = bevelBox(d.w + 0.008, 0.012, d.d + 0.006, 0.003);
      top.translate(0, d.h + 0.004, 0);
      w.add(top, 'steel', 0.4);

      w.part('굽');
      const plinth = bevelBox(d.w - 0.03, 0.04, d.d - 0.03, 0.004);
      plinth.translate(0, 0.02, 0);
      w.add(plinth, 'steel', 0.3);

      /* **서랍 두 짝을 도로 들여왔다** (2026-09-11). 예전에 뺀 이유는 「앞판이 몸통과 한
       * 덩어리면 프롭이 못 움직인다」였는데, 이제 `w.movable` 이 병합에서 빼 주므로 한 조각에
       * 둘 수 있다. 형태는 `lockDrawerBuild` 를 그대로 부른다 — 복사하면 둘이 갈라진다.
       * 아래 칸은 y=0.05+T, 위 칸은 그 위 칸막이 위. 서랍 dims 는 조각과 같은 값. */
      const dd0 = { w: 0.446, h: 0.305, d: 0.56, face: 0.014 };
      w.movable('아래 서랍', () => { w.at(0, 0.05 + T / 2, 0, () => lockDrawerBuild(w, dd0)); });
      w.movable('위 서랍', () => { w.at(0, 0.055 + dh0 + T / 2, 0, () => lockDrawerBuild(w, dd0)); });
      w.part('잠금 바');
      // 해제 위치 — 바가 위로 올라와 있다. 이 5cm 가 E15·E16 의 근거다
      const bar = bevelBox(0.02, d.h - 0.13, 0.014, 0.003);
      bar.translate(d.w / 2 - 0.024, d.h / 2 + 0.05, -d.d / 2 - 0.02);
      w.add(bar, 'alu', 0.15);

      w.part('다이얼');
      const dial = lathe(
        [
          [0, 0],
          [0.021, 0],
          [0.023, 0.006],
          [0.021, 0.014],
          [0, 0.016],
        ],
        16,
      );
      dial.rotateX(-Math.PI / 2);
      dial.translate(d.w / 2 - 0.024, d.h - 0.055, -d.d / 2 - 0.02);
      w.add(dial, 'steel', 0.1);
      const notch = bevelBox(0.0035, 0.0035, 0.02, 0.0008);
      notch.rotateZ(0.6);
      notch.translate(d.w / 2 - 0.024, d.h - 0.041, -d.d / 2 - 0.028);
      w.add(notch, 'alu', 0.05);
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          g.strokeStyle = 'rgba(30,38,52,0.8)';
          stroke(g, W * 0.06, H * 0.62, W * 0.72, H * 0.62, H * 0.16, 3);
          num(g, '15', W * 0.92, H * 0.72, H * 0.44, '#2a3444', 'right');
        },
        256,
        64,
        '#e6e0d0',
      ),
    }),
  },


  {
    id: 'glaze_cabinet',
    name: '유약 캐비닛',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E26. 작업장의 유리문 유약장. **문은 -Z**. 손잡이에 남경정이 걸어 둔 자물쇠가 물려 있고, 안쪽 두 번째 칸의 통 하나만 뚜껑이 비뚤다 — 잠긴 장 안에서 무언가가 손 탔다는 게 형태로 보여야 한다. 자물쇠 고리는 반원 띠로 만들어 양 끝을 몸통에 묻는다(온전한 원환을 반쯤 묻으면 유리 너머로 아래 반이 보인다). */
    note: '유리문이 달린 유약장. **문은 -Z**. 손잡이에 자물쇠가 물려 있고, 안쪽 선반에 유약통이 줄지어 있으며 두 번째 칸의 통 하나만 뚜껑이 비뚤다. 자물쇠 고리는 반원 띠로 만들어 양 끝을 몸통에 묻는다(온전한 원환을 반쯤 묻으면 유리 너머로 아래 반이 보인다). dims 는 w·h·d.',
    dims: { w: 0.9, h: 1.45, d: 0.4 },
    /* 유리문 두 짝을 여닫는다. 세로 경첩이라 y 축이고, 경첩은 몸통 바깥 모서리다.
     * 축 좌표는 정적이라 dims(w 0.9 · h 1.45 · d 0.4)에서 나온 수를 적는다:
     * x = ±w/2 = ±0.45 · y = h/2 = 0.725 · z = −d/2 + 0.009 = −0.191
     * 담는 곳은 두 번째 칸(y = h/2 = 0.725) — 유약통 옆에 물건을 놓을 수 있다 */
    openable: [
      { part: '왼짝', swing: 1.45, axis: 'y', pivot: [-0.45, 0.725, -0.191],
        inside: { w: 0.8, h: 0.16, d: 0.3, at: [0, 0.78, 0] } },
      { part: '오른짝', swing: -1.45, axis: 'y', pivot: [0.45, 0.725, -0.191] },
    ],
    clusters: 1,
    build(w, d) {
      const t = 0.016;

      w.part('몸통');
      w.at(0, d.h / 2, 0, () => {
        const side: [number, number, number, number, number, number][] = [
          [-d.w / 2 + t / 2, 0, 0, t, d.h, d.d],
          [d.w / 2 - t / 2, 0, 0, t, d.h, d.d],
          [0, d.h / 2 - t / 2, 0, d.w, t, d.d],
          [0, -d.h / 2 + t / 2, 0, d.w, t, d.d],
          [0, 0, d.d / 2 - t / 2, d.w - t * 2, d.h - t * 2, t],
        ];
        for (const [x, y, z, bw, bh, bd] of side) {
          const g = bevelBox(bw, bh, bd, 0.003);
          g.translate(x, y, z);
          w.add(g, 'steel', 0.4);
        }
      });

      w.part('선반');
      for (let i = 1; i <= 3; i++) {
        const sh = bevelBox(d.w - t * 2, 0.01, d.d - t * 2 - 0.01, 0.002);
        sh.translate(0, (d.h * i) / 4, 0.004);
        w.add(sh, 'steel', 0.3);
      }

      w.part('유약통');
      for (let r = 0; r < 3; r++) {
        const y = (d.h * (r + 1)) / 4 + 0.005;
        const n = 5;
        for (let i = 0; i < n; i++) {
          const rad = 0.045 + rnd(r * 7 + i) * 0.016;
          const hh = 0.11 + rnd(r * 3 + i) * 0.05;
          const jar = lathe(
            [
              [0, 0],
              [rad, 0.004],
              [rad, hh - 0.01],
              [rad * 0.86, hh],
              [0, hh],
            ],
            14,
          );
          const x = -d.w / 2 + 0.09 + (i * (d.w - 0.19)) / (n - 1);
          jar.translate(x, y, (rnd(r + i * 5) - 0.5) * 0.06);
          // 유약장인데 통이 전부 같은 흰색이면 「흰 원통 선반」이다. 슬롯을 늘리지 않고
          // 정점색만 구워 통마다 다른 유약을 담게 한다 — 드로우콜도 재질도 그대로다
          paintGeometry(jar, JAR_TINT[(r * 5 + i * 3) % JAR_TINT.length]);
          w.add(jar, 'porcelain', 0.12);

          // 두 번째 칸 가운데 통만 뚜껑이 비뚤다
          const askew = r === 1 && i === 2;
          const lid = lathe(
            [
              [0, 0],
              [rad * 1.06, 0],
              [rad * 1.06, 0.008],
              [rad * 0.9, 0.012],
              [0, 0.012],
            ],
            14,
          );
          if (askew) lid.rotateZ(0.26);
          lid.translate(x + (askew ? rad * 0.4 : 0), y + hh - 0.004 + (askew ? 0.006 : 0), (rnd(r + i * 5) - 0.5) * 0.06);
          w.add(lid, 'plastic', 0.1);
        }
      }

      /* 짝마다 **틀·유리·손잡이를 한 덩어리로** 묶는다 — 문이 열릴 때 손잡이가 제자리에 남으면
       * 유리만 날아간 꼴이 된다. 자물쇠는 왼짝에 딸려 간다(고리가 풀린 채 걸려 있는 상태) */
      for (const s of [-1, 1] as const) {
        w.movable(s < 0 ? '왼짝' : '오른짝', () => {
          const fw = d.w / 2 - 0.004;
          const frame: [number, number, number, number][] = [
            [0, d.h / 2 - 0.018, fw, 0.036],
            [0, -d.h / 2 + 0.018, fw, 0.036],
            [(-s * fw) / 2 + s * 0.018, 0, 0.036, d.h - 0.072],
            [(s * fw) / 2 - s * 0.018, 0, 0.036, d.h - 0.072],
          ];
          for (const [x, y, bw, bh] of frame) {
            const g = bevelBox(bw, bh, 0.018, 0.002);
            g.translate((s * d.w) / 4 + x, d.h / 2 + y, -d.d / 2 + 0.009);
            w.add(g, 'steel', 0.3);
          }
          const pane = bevelBox(fw - 0.06, d.h - 0.09, 0.005, 0.001);
          pane.translate((s * d.w) / 4, d.h / 2, -d.d / 2 + 0.009);
          w.add(pane, 'glass', 0.3);

          const hd = bevelBox(0.014, 0.16, 0.014, 0.003);
          hd.translate(-s * 0.026, d.h * 0.52, -d.d / 2 - 0.012);
          w.add(hd, 'alu', 0.1);
          for (const dy of [-0.07, 0.07]) {
            const arm = bevelBox(0.01, 0.012, 0.022, 0.002);
            arm.translate(-s * 0.026, d.h * 0.52 + dy, -d.d / 2 - 0.003);
            w.add(arm, 'alu', 0.1);
          }

          if (s < 0) {
            const lockY = d.h * 0.52;
            const lockZ = -d.d / 2 - 0.028;
            const lb = bevelBox(0.044, 0.056, 0.017, 0.004);
            lb.translate(0, lockY - 0.03, lockZ);
            w.add(lb, 'steel', 0.1);
            // 고리는 **반원 띠**로 만들고 양 끝을 자물쇠 몸통 안에 묻는다
            const arc: THREE.Vector3[] = [];
            for (let i = 0; i <= 14; i++) {
              const a = Math.PI * (i / 14);
              arc.push(new THREE.Vector3(-Math.cos(a) * 0.015, lockY - 0.006 + Math.sin(a) * 0.019, lockZ));
            }
            ribbon(w, arc, 0.006, 0.006, 'alu');
          }
        });
      }
    },
    materials: () => ({}),
  },

  {
    id: 'product_box',
    nofront: true,
    name: '미회수 제품 상자',
    collection: 'sahol',
    category: '단서',
    note:
      'E27·E47. 반품 라벨이 붙은 채 뜯긴 골판지 상자. 안에 완충 종이와 도자기 하나가 들어 있다.\n' +
      '**플랩을 이 조각이 안고 있다** (2026-09-11) — 예전엔 몸통과 덮개(`product_box_flaps`)가 따로라 ' +
      '상자를 옮기면 뚜껑이 따로 놀았다. 이제 위 짝 두 개가 `w.movable` 로 남고 `openable` 이 ' +
      '**양옆으로 젖히는 것**을 말한다. 반품 박스라 가운데 테이프가 칼로 갈려 있어 양쪽이 갈린다 — ' +
      '테이프가 성한 상자라면 젖히는 게 아니라 옆으로 미는 것이 맞다.\n' +
      '네 짝이 다 젖혀진다 — 안쪽 짝(앞·뒤)을 고정으로 두었더니 둘이 가운데서 만나 입구를 덮어 「열렸는데 안이 안 보인다」가 됐다(2026-09-12, Chris).',
    /* 플랩 넷 중 **위 짝 둘만** 움직인다. 회전축은 상자 테두리(y=0.27)의 좌·우 끝.
     * pivot 은 정적 값이라 dims 를 못 읽는다 — w 0.34 · h 0.27 에서 나온 수를 그대로 적는다 */
    openable: [
      /* 담는 곳은 상자 속(0.33×0.24×0.33). 왼 플랩에 달아 두면 에디터가 그 위로 끄는 물건을
       * 「상자 안에」로 잡는다 — 플랩을 젖혀야 보이므로 안의 물건도 같이 감춰졌다 드러난다 */
      /* **2.1 라디안은 지붕이 된다** — 두 짝이 위로 솟아 속이 안 보였다(2026-09-12 열림 컷).
       * 2.9(166°)면 옆구리를 타고 내려와 입구가 통째로 열린다. 실제 상자도 그렇게 젖혀진다 */
      { part: '왼 플랩', swing: -2.9, pivot: [-0.17, 0.27, 0], inside: { w: 0.32, h: 0.22, d: 0.32, at: [0, 0.14, 0] } },
      { part: '오른 플랩', swing: 2.9, pivot: [0.17, 0.27, 0] },
      /* **안쪽 짝도 젖혀져야 속이 보인다** (2026-09-12, Chris: 「이건 열렸는데 왜 안이 안보이냐」).
       * 두 짝이 각각 깊이의 절반을 덮어 가운데서 만나므로, 고정이면 위 짝을 젖혀도 그 밑이
       * 그대로 막혀 있다. 앞뒤로(x 축) 테두리에서 젖힌다 — 실제 상자를 여는 순서 그대로다. */
      { part: '앞 플랩', swing: -2.9, axis: 'x', pivot: [0, 0.27, -0.17] },
      { part: '뒤 플랩', swing: 2.9, axis: 'x', pivot: [0, 0.27, 0.17] },
    ],
    dims: { w: 0.34, h: 0.27, d: 0.34 },
    clusters: 1,
    build(w, d) {
      const t = 0.005;

      w.part('상자');
      const wall: [number, number, number, number, number, number][] = [
        [0, d.h / 2, -d.d / 2 + t / 2, d.w, d.h, t],
        [0, d.h / 2, d.d / 2 - t / 2, d.w, d.h, t],
        [-d.w / 2 + t / 2, d.h / 2, 0, t, d.h, d.d - t * 2],
        [d.w / 2 - t / 2, d.h / 2, 0, t, d.h, d.d - t * 2],
        [0, t / 2, 0, d.w, t, d.d],
      ];
      for (const [x, y, z, bw, bh, bd] of wall) {
        const g = bevelBox(bw, bh, bd, 0.002);
        g.translate(x, y, z);
        w.add(g, 'cardboard', 0.3);
      }

      /* **플랩 넷을 도로 들여왔다** (2026-09-11). 예전에 뺀 이유는 「덮개가 몸통과 한 덩어리면
       * 프롭이 그것만 못 움직인다」였는데, 이제 `w.movable` 이 병합에서 빼 주므로 한 조각으로 둘 수 있다.
       * 아래 짝(긴 변)은 먼저 접혀 들어가 위 짝 밑에 깔린다 — 고정이다.
       * 위 짝 둘은 각각 제 그룹으로 남아 양옆으로 젖혀진다. */
      const ft = 0.0042, fhalf = ft / 2, fgap = 0.0015;
      /* 안쪽 짝(짧은 변) — 먼저 접혀 들어가 위 짝 밑에 깔린다. 둘이 가운데서 만나 입구를 덮으므로
       * **이것도 움직여야** 위 짝을 젖혔을 때 속이 보인다(2026-09-12) */
      for (const sgn of [-1, 1] as const) {
        const len = d.d * 0.53;
        w.movable(sgn < 0 ? '앞 플랩' : '뒤 플랩', () => {
          const g = bevelBox(d.w, ft, len, 0.0015);
          g.translate(0, d.h + fhalf, sgn * (d.d / 2 - len / 2));
          w.add(g, 'cardboard', 0.3);
        });
      }
      for (const sgn of [-1, 1] as const) {
        const len = d.w / 2 - fgap / 2;
        const tilt = -sgn * 0.03;          // 테이프가 갈려 자유변이 들뜬다
        const cx = sgn * (d.w / 2 - len / 2);
        w.movable(sgn < 0 ? '왼 플랩' : '오른 플랩', () => {
          const g = bevelBox(len, ft, d.d, 0.0015);
          g.rotateZ(tilt);
          g.translate(cx, d.h + ft + fhalf, 0);
          w.add(g, 'cardboard', 0.3);
          // 갈린 포장 테이프 — 판과 똑같은 변환을 먹여야 판 속에 안 묻힌다(api.md 21)
          const tw = 0.023;
          const tp = bevelBox(tw, 0.0013, d.d, 0.0004);
          tp.translate(-sgn * (len / 2 - tw / 2 - 0.0015), ft / 2 + 0.00065, 0);
          tp.rotateZ(tilt);
          tp.translate(cx, d.h + ft + fhalf, 0);
          paintGeometry(tp, '#e6dcc2');
          w.add(tp, 'paper', 0.2);
        });
      }

      w.part('완충 종이');
      for (let i = 0; i < 7; i++) {
        const cr = bevelBox(0.08 + rnd(i) * 0.06, 0.03 + rnd(i * 3) * 0.03, 0.07 + rnd(i * 5) * 0.06, 0.008, 1);
        cr.rotateY(rnd(i * 7) * 3.1);
        cr.rotateX(rnd(i * 11) * 0.6);
        cr.translate((rnd(i * 2) - 0.5) * 0.16, t + 0.03 + rnd(i * 4) * 0.05, (rnd(i * 6) - 0.5) * 0.16);
        w.add(cr, 'paper', 0.14);
      }

      w.part('제품');
      const pot = lathe(
        [
          [0, 0],
          [0.045, 0.004],
          [0.072, 0.05],
          [0.066, 0.1],
          [0.042, 0.13],
          [0.046, 0.138],
          [0.04, 0.14],
          [0.038, 0.06],
          [0, 0.03],
        ],
        18,
      );
      pot.rotateZ(0.28);
      pot.translate(0.012, t + 0.075, -0.01);
      w.add(pot, 'porcelain', 0.1);

      w.part('라벨');
      w.thinSurface(() => {
        const p = facingFront(d.w * 0.56, d.h * 0.42);
        p.translate(-d.w * 0.1, d.h * 0.52, -d.d / 2 - 0.0006);
        w.add(p, 'painted', 0);
      });

      w.part('스티커');
      const st = bevelBox(0.072, 0.072, 0.0008, 0.0004);
      st.rotateZ(0.42);
      st.translate(d.w * 0.28, d.h * 0.3, -d.d / 2 - 0.0004);
      w.add(st, 'redcloth', 0.06);
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          g.strokeStyle = 'rgba(40,50,66,0.55)';
          g.lineWidth = 2.5;
          g.strokeRect(W * 0.04, H * 0.05, W * 0.92, H * 0.9);
          g.beginPath();
          g.moveTo(W * 0.04, H * 0.42);
          g.lineTo(W * 0.96, H * 0.42);
          g.stroke();
          for (let i = 0; i < 3; i++) {
            stroke(g, W * 0.09, H * (0.16 + i * 0.09), W * (0.35 + rnd(i) * 0.5), H * (0.16 + i * 0.09), H * 0.028, i);
          }
          // 바코드 — 굵기가 균일하면 줄무늬로 보인다. 폭을 세 단계로 흔든다
          let x = W * 0.09;
          g.fillStyle = '#1b2130';
          while (x < W * 0.62) {
            const bw = 2 + Math.floor(rnd(x) * 3) * 2.5;
            g.fillRect(x, H * 0.52, bw, H * 0.24);
            x += bw + 2 + rnd(x * 3) * 4;
          }
          num(g, '2024.12.18', W * 0.94, H * 0.62, H * 0.1, '#1b2130', 'right');
          num(g, '2 / 6', W * 0.94, H * 0.76, H * 0.09, '#8a3428', 'right');
          stroke(g, W * 0.09, H * 0.86, W * 0.5, H * 0.86, H * 0.03, 9);
        },
        512,
        380,
        '#efe6d2',
      ),
    }),
  },


  {
    id: 'breaker_panel',
    mount: 'wall',
    name: '분전반',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E23. 작업실 벽의 분전반. **원점이 걸이 지점**이라 함이 y=0 아래로 내려간다(hangs + sunk). 문이 열려 차단기 열이 보이고 **오른쪽에서 두 번째 하나만 내려가 있다** — 16일 밤 그 회로가 끊겼다는 게 단서의 전부다. 문은 Y 축 경첩으로 도니 무게중심 z 가 계속 음수라 앞뒤 게이트를 통과한다. */
    note: '벽에 걸린 분전반. **원점이 걸이 지점**이라 함이 y=0 아래로 내려간다(hangs + sunk). 문이 열려 차단기 열이 보인다 — 배치의 tripped(회로 라벨)가 주어진 패널만 그 스위치가 내려간다(v8 A6, 발전기실 L-07). 문은 Y 축 경첩으로 도니 무게중심 z 가 계속 음수라 앞뒤 게이트를 통과한다. dims 는 w·h 와 함 두께 t.',
    hangs: true,
    sunk: true,
    dims: { w: 0.34, h: 0.44, t: 0.1 },
    clusters: 1,
    /* 문은 왼쪽(-x) 경첩에서 1.75 rad 열린 채 구워 있다 — E 를 누르면 닫힌다 */
    openable: [{ part: '문', swing: -1.75, axis: 'y', pivot: [-0.17, -0.22, -0.045], inside: { w: 0.28, h: 0.36, d: 0.06, at: [0, -0.22, 0.02] } }],
    build(w, d, s) {
      const cy = -d.h / 2;
      const t = 0.008;

      w.part('함');
      const wall: [number, number, number, number, number, number][] = [
        [0, cy + d.h / 2 - t / 2, 0, d.w, t, d.t],
        [0, cy - d.h / 2 + t / 2, 0, d.w, t, d.t],
        [-d.w / 2 + t / 2, cy, 0, t, d.h - t * 2, d.t],
        [d.w / 2 - t / 2, cy, 0, t, d.h - t * 2, d.t],
        [0, cy, d.t / 2 - t / 2, d.w - t * 2, d.h - t * 2, t],
      ];
      for (const [x, y, z, bw, bh, bd] of wall) {
        const g = bevelBox(bw, bh, bd, 0.002);
        g.translate(x, y, z);
        w.add(g, 'steel', 0.3);
      }

      w.part('베이스판');
      const base = bevelBox(d.w - 0.03, d.h - 0.05, 0.006, 0.002);
      base.translate(0, cy, d.t * 0.18);
      w.add(base, 'plastic', 0.2);

      w.part('차단기');
      const n = 8;
      /* **내려간 차단기는 자리가 정한다** (사흘 v8 A6) — 배치의 `tripped`(회로 라벨
       * 'L-07')이 오면 그 번호의 스위치만 내린다. 라벨 숫자 → 1부터 세는 회로 번호
       * ('L-07' → 7번 → index 6, 오른쪽에서 두 번째). tripped 가 없는 패널(목공실)은
       * 전부 올라가 있다 — 내려간 게 보이는 것 자체가 단서다. */
      const tripNum = s?.tripped ? Number(s.tripped.replace(/\D/g, '')) : 0;
      const trip = s?.tripped ? Math.min(n, Math.max(1, tripNum || n - 1)) - 1 : -1;
      const bw2 = (d.w - 0.06) / n;
      for (let i = 0; i < n; i++) {
        const x = -d.w / 2 + 0.03 + bw2 * (i + 0.5);
        const bd = bevelBox(bw2 - 0.0025, 0.062, 0.032, 0.0015);
        bd.translate(x, cy + 0.03, d.t * 0.18 - 0.019);
        w.add(bd, 'plastic', 0.1);
        const off = i === trip;
        const lv = bevelBox(bw2 * 0.5, 0.014, 0.012, 0.0012);
        lv.rotateX(off ? 0.5 : -0.5);
        lv.translate(x, cy + 0.03 + (off ? -0.016 : 0.016), d.t * 0.18 - 0.038);
        w.add(lv, off ? 'redcloth' : 'alu', 0.06);
      }

      w.part('배선');
      for (let i = 0; i < 6; i++) {
        const pts = spline(
          [
            [-d.w / 2 + 0.03 + bw2 * (i + 0.5), cy + 0.06, d.t * 0.18 - 0.014],
            [-d.w / 2 + 0.04 + i * 0.008, cy + 0.11, d.t * 0.2],
            [-d.w * 0.1 + i * 0.006, cy + 0.16, d.t * 0.28],
          ],
          8,
        );
        ribbon(w, pts, 0.0032, 0.0032, i % 3 === 0 ? 'redcloth' : 'plastic');
      }

      w.part('회로 표');
      w.thinSurface(() => {
        const p = facingFront(d.w - 0.07, 0.075);
        p.translate(0, cy - 0.11, d.t * 0.18 - 0.004);
        w.add(p, 'painted', 0);
      });

      w.part('문');
      // 왼쪽 모서리 경첩으로 100° 연다. Y 축 회전이라 무게중심 z 는 계속 음수다
      const ang = 1.75;
      w.movable('문', () => {
        const door = bevelBox(d.w, d.h, 0.01, 0.0025);
        door.translate(d.w / 2, 0, 0);
        door.rotateY(ang);
        door.translate(-d.w / 2, cy, -d.t / 2 + 0.005);
        w.add(door, 'steel', 0.3);
        const knob = bevelBox(0.01, 0.03, 0.014, 0.002);
        knob.translate(d.w - 0.03, 0, -0.008);
        knob.rotateY(ang);
        knob.translate(-d.w / 2, cy, -d.t / 2 + 0.005);
        w.add(knob, 'alu', 0.06);
      });
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          g.strokeStyle = 'rgba(30,38,50,0.5)';
          g.lineWidth = 1.4;
          for (let i = 0; i <= 8; i++) {
            g.beginPath();
            g.moveTo((W * i) / 8, 0);
            g.lineTo((W * i) / 8, H);
            g.stroke();
          }
          g.beginPath();
          g.moveTo(0, H * 0.42);
          g.lineTo(W, H * 0.42);
          g.stroke();
          for (let i = 0; i < 8; i++) {
            num(g, String(i + 1), (W * (i + 0.5)) / 8, H * 0.34, H * 0.26, '#232c3c', 'center');
            stroke(g, (W * i) / 8 + W * 0.02, H * 0.66, (W * (i + 1)) / 8 - W * 0.02, H * 0.66, H * 0.1, i * 3);
          }
          g.fillStyle = 'rgba(168,52,42,0.28)';
          g.fillRect((W * 6) / 8, 0, W / 8, H);
        },
        640,
        160,
        '#e9e3d4',
      ),
    }),
  },

  {
    id: 'sample_jar',
    mount: 'surface',
    nofront: true,
    name: '보존샘플 시료병',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E29. 파편통에서 건져 봉인한 시료병 둘. 유리가 투명해야 안의 파편이 보이고, 그래야 「무엇을 보존했는지」가 형태로 읽힌다 — 불투명하게 두면 그냥 병 두 개다. 뚜껑 위로 봉인 테이프가 가로질러 있고 몸통에 채취일 라벨이 붙어 있다. 큰 병에만 파편이 들었고 작은 병은 비어 있다. */
    note: '봉인된 시료병 둘. 유리가 투명해야 안의 내용물이 보이고, 그래야 「무엇을 보존했는지」가 형태로 읽힌다 — 불투명하게 두면 그냥 병 두 개다. 뚜껑 위로 봉인 테이프가 가로질러 있고 몸통에 날짜 라벨이 붙어 있다. 큰 병에만 파편이 들었고 작은 병은 비어 있다. dims 는 반지름 r 과 높이 h.',
    dims: { r: 0.031, h: 0.088 },
    clusters: 2,
    /* 봉인 테이프째 뚜껑을 들어 올린다 — 큰 병에는 파편이 들어 있다 */
    openable: [
      { part: '뚜껑 1', slide: [0, 0.05, 0], inside: { w: 0.04, h: 0.06, d: 0.04, at: [-0.026, 0.04, 0] } },
      { part: '뚜껑 2', slide: [0, 0.04, 0], inside: { w: 0.026, h: 0.04, d: 0.026, at: [0.03, 0.026, 0.026] } },
    ],
    build(w, d) {
      const jar = (cx: number, cz: number, sc: number, fill: boolean, seed: number, n: number) => {
        const R = d.r * sc;
        const H = d.h * sc;
        w.part('병');
        const body = lathe(
          [
            [0, 0],
            [R - 0.003, 0],
            [R, 0.004],
            [R, H - 0.014],
            [R * 0.78, H - 0.006],
            [R * 0.78, H],
            [0, H],
          ],
          20,
        );
        body.translate(cx, 0, cz);
        w.add(body, 'glass', 0.1);

        w.part('뚜껑');
        w.movable('뚜껑 ' + n, () => {
          const cap = lathe(
            [
              [0, 0],
              [R * 0.84, 0],
              [R * 0.84, 0.014],
              [R * 0.7, 0.017],
              [0, 0.017],
            ],
            20,
          );
          cap.translate(cx, H - 0.008, cz);
          w.add(cap, 'plastic', 0.08);

          w.part('봉인 테이프');
          const tape = bevelBox(R * 1.9, 0.0008, 0.012, 0.0003);
          tape.rotateY(0.4);
          tape.translate(cx, H + 0.0095, cz);
          w.add(tape, 'painted', 0.1);
        });

        w.part('라벨');
        w.thinSurface(() => {
          const p = wrapLabel(R + 0.0006, 2.1, H * 0.42);
          p.translate(cx, H * 0.4, cz);
          w.add(p, 'painted', 0);
        });

        if (fill) {
          w.part('파편');
          for (let i = 0; i < 5; i++) {
            const sd = shard(seed + i * 7, R * (0.4 + rnd(seed + i) * 0.4), 0.0035, 3);
            sd.rotateX(rnd(seed + i * 3) * 0.9);
            sd.rotateY(rnd(seed + i * 5) * 3.1);
            sd.translate(
              cx + (rnd(seed + i * 2) - 0.5) * R * 0.7,
              0.004 + i * 0.0055,
              cz + (rnd(seed + i * 4) - 0.5) * R * 0.7,
            );
            w.add(sd, 'porcelain', 0.05);
          }
        }
      };

      jar(-0.026, 0, 1, true, 11, 1);
      jar(0.03, 0.026, 0.66, false, 53, 2);
    },
    materials: () => ({
      // 슬롯 `glass` 는 기본이 불투명이다. 안이 보여야 하는 병에서만 국소로 덮어쓴다
      glass: new THREE.MeshStandardMaterial({
        color: new THREE.Color('#cfe0dc'),
        roughness: 0.08,
        metalness: 0,
        transparent: true,
        opacity: 0.34,
        side: THREE.DoubleSide,
        vertexColors: true,
      }),
      painted: printedFace(
        (g, W, H) => {
          g.fillStyle = 'rgba(150,40,32,0.18)';
          g.fillRect(0, 0, W, H * 0.24);
          num(g, 'E-29', W * 0.06, H * 0.19, H * 0.2, '#8c2f26');
          num(g, '2024.12.19', W * 0.94, H * 0.19, H * 0.16, '#8c2f26', 'right');
          g.strokeStyle = 'rgba(36,44,58,0.75)';
          for (let i = 0; i < 3; i++) {
            stroke(g, W * 0.07, H * (0.42 + i * 0.19), W * (0.4 + rnd(i * 3) * 0.5), H * (0.42 + i * 0.19), H * 0.055, i * 5);
          }
          g.strokeStyle = 'rgba(36,44,58,0.45)';
          g.lineWidth = 2;
          g.strokeRect(W * 0.03, H * 0.03, W * 0.94, H * 0.94);
        },
        320,
        180,
        '#f0ebdd',
      ),
    }),
  },

  /**
   * HS04 — 충전기와 케이블.
   *
   * 초안 98행에는 있는데 조각이 없던 것. 「휴대폰이 충전기 **바로 옆**에 있는데 케이블은
   * 연결돼 있지 않다」가 단서 전부라, **커넥터 끝이 허공을 향해 놓여 있는 것**이 이
   * 조각의 유일한 임무다. 끝을 폰 쪽에 붙여 놓으면 단서가 정반대로 읽힌다.
   */
  {
    id: 'phone_charger',
    name: '충전기·케이블',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: HS04. 휴대폰 옆에 놓인 충전 어댑터와 감긴 케이블. **커넥터 끝은 어디에도 꽂혀 있지 않고 코일 밖으로 빠져나와 바닥에 그대로 놓여 있어야 한다** — 이 물건의 단서는 「옆에 있는데 연결은 안 돼 있다」 하나뿐이다. 케이블은 감아 두었던 느슨한 코일이고 중간에 케이블 타이가 하나 물려 있다. 상표 금지 — 로고·글자마크 없이 무지 몸통과 둥근 핀 둘만 쓴다. */
    note: '충전 어댑터와 감아 둔 케이블. 케이블은 느슨한 코일이고 중간에 케이블 타이가 하나 물려 있으며, **커넥터 끝은 코일 밖으로 빠져나와 바닥에 그대로 놓여 어디에도 꽂혀 있지 않다**. 상표 금지 — 로고·글자마크 없이 무지 몸통과 둥근 핀 둘만 쓴다. dims 는 w·h·d.',
    dims: { w: 0.135, h: 0.032, d: 0.078 },
    /* 충전기를 집어 든다 — 코일째 통으로 들린다 (2026-09-16) */
    openable: [{ part: '충전기', slide: [0, 0, -0.05] }],
    clusters: 1,
    build(w) {
      w.movable('충전기', () => {
      w.part('어댑터');
      const ad = bevelBox(0.032, 0.03, 0.034, 0.004, 2);
      ad.translate(-0.042, 0.015, 0);
      w.add(ad, 'paint', 0.06);

      w.part('플러그 핀');
      // 둥근 핀 둘. 각진 날 두 장으로 만들면 특정 규격을 지목하게 된다
      for (const dz of [-0.0075, 0.0075]) {
        const pin = new THREE.CylinderGeometry(0.0022, 0.0022, 0.015, 8);
        pin.rotateZ(Math.PI / 2);
        pin.translate(-0.065, 0.016, dz);
        w.add(pin, 'alu', 0.05);
      }

      // 케이블 — 어댑터 속에서 나와 느슨한 코일을 그리고 접선 방향으로 빠져나간다
      const pts: [number, number, number][] = [
        [-0.04, 0.013, 0.009],
        [-0.031, 0.006, 0.019],
      ];
      const cx = 0.026;
      const cz = 0.002;
      const y0 = 0.0028;
      const turns = 1.55;
      let ex = 0;
      let ez = 0;
      let ea = 0;
      for (let i = 0; i <= 30; i++) {
        const t = i / 30;
        const a = 2.5 + t * turns * Math.PI * 2;
        const r = 0.033 - t * 0.011;
        ex = cx + Math.cos(a) * r;
        ez = cz + Math.sin(a) * r;
        ea = a;
        pts.push([ex, y0 + Math.sin(t * 5.2) * 0.0011, ez]);
      }
      // 접선 — 코일에서 빠져나오는 방향
      const tx = -Math.sin(ea);
      const tz = Math.cos(ea);
      pts.push([ex + tx * 0.013, y0, ez + tz * 0.013]);
      pts.push([ex + tx * 0.027, 0.0032, ez + tz * 0.027]);

      w.part('케이블');
      // 관은 양 끝이 뚫려 있다. 시작은 어댑터 속, 끝은 커넥터 속에 묻어 닫힘 검사에서 뺀다
      const curve = new THREE.CatmullRomCurve3(pts.map((q) => new THREE.Vector3(...q)));
      w.thinSurface(() => w.add(new THREE.TubeGeometry(curve, 46, 0.0022, 6, false), 'rubber', 0.6));

      w.part('케이블 타이');
      // 「감아 두었던 것」이 형태로 남아야 한다 — 타이가 없으면 그냥 흘린 줄이다
      const ma = 2.5 + 0.5 * turns * Math.PI * 2;
      const mr = 0.033 - 0.0055;
      const tie = new THREE.TorusGeometry(0.0042, 0.0012, 6, 12);
      tie.rotateY(Math.atan2(-Math.sin(ma), Math.cos(ma)));
      tie.translate(cx + Math.cos(ma) * mr, y0, cz + Math.sin(ma) * mr);
      w.add(tie, 'plastic', 0.05);

      w.part('커넥터');
      const th = Math.atan2(tx, tz);
      const con = bevelBox(0.0105, 0.006, 0.017, 0.0016);
      con.rotateY(th);
      con.translate(ex + tx * 0.027, 0.003, ez + tz * 0.027);
      w.add(con, 'plastic', 0.05);
      const tip = bevelBox(0.0072, 0.0034, 0.008, 0.0008);
      tip.rotateY(th);
      tip.translate(ex + tx * 0.0385, 0.0028, ez + tz * 0.0385);
      w.add(tip, 'alu', 0.04);
      });
    },
  },
];
